/**
=========================================================
* Elite 247 Admin Panel - Modernized Consultations
=========================================================
*/

import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import RecordTable from "components/RecordTable";

import { adminApi } from "services/api";
import PaymentLinkModal from "components/PaymentLinkModal";
import CallButton from "components/CallButton";
import { consultationsToRows } from "utils/exportUtils";

const STATUS_FILTER_OPTIONS = ["PENDING", "SCHEDULED", "COMPLETED", "CANCELLED"].map((s) => ({
  value: s,
  label: s.charAt(0) + s.slice(1).toLowerCase(),
}));

const EXPORT_CONFIG = {
  fetchAll: adminApi.exportConsultations,
  toRows: consultationsToRows,
  filename: "consultations",
  title: "Consultations",
};

// Below md the table becomes stacked cards: give row actions ~40px tap targets there.
const ROW_ACTION_SX = { width: { xs: 40, md: "auto" }, height: { xs: 40, md: "auto" } };

function Consultations() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [reloadToken, setReloadToken] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Errors from a dialog's own action show inside that dialog, not behind it on the page.
  const [dialogError, setDialogError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [paymentLinkTarget, setPaymentLinkTarget] = useState(null);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    subject: "",
    description: "",
    startDateTime: "",
    endDateTime: "",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    attendees: "",
    meetingType: "one-time",
    recurrencePattern: "weekly",
    recurrenceInterval: 1,
    recurrenceEndDate: "",
    sendEmail: true,
  });
  const [meetingSubmitting, setMeetingSubmitting] = useState(false);

  const handleViewDetails = (consultation) => {
    setSelectedConsultation(consultation);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedConsultation(null);
  };

  const handleDeleteClick = (consultation) => {
    setDialogError("");
    setSelectedConsultation(consultation);
    setOpenDeleteDialog(true);
  };

  const openMeetingDialog = (consultation) => {
    setDialogError("");
    setSelectedConsultation(consultation);
    setMeetingForm((prev) => ({
      ...prev,
      subject: `Consultation with ${consultation?.name || "client"}`,
      description: `Consultation for ${consultation?.service || "your request"}.`,
      attendees: consultation?.email ? consultation.email : "",
      startDateTime: "",
      endDateTime: "",
    }));
    setMeetingDialogOpen(true);
  };

  const handleCreateMeeting = async () => {
    try {
      setMeetingSubmitting(true);
      setDialogError("");
      setSuccess("");
      const payload = {
        ...meetingForm,
        attendees: meetingForm.attendees
          .split(/[;,\n]+/)
          .map((item) => item.trim())
          .filter(Boolean),
        startDateTime: meetingForm.startDateTime,
        endDateTime: meetingForm.endDateTime,
        recurrencePattern:
          meetingForm.meetingType === "recurring" ? meetingForm.recurrencePattern : undefined,
        recurrenceInterval: Number(meetingForm.recurrenceInterval) || 1,
        recurrenceEndDate: meetingForm.recurrenceEndDate || undefined,
      };
      const response = await adminApi.createTeamsMeeting(payload);
      setSuccess(`Teams meeting created${response?.meeting?.joinUrl ? ": join link ready" : ""}`);
      setMeetingDialogOpen(false);
      setMeetingForm({
        subject: "",
        description: "",
        startDateTime: "",
        endDateTime: "",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        attendees: "",
        meetingType: "one-time",
        recurrencePattern: "weekly",
        recurrenceInterval: 1,
        recurrenceEndDate: "",
        sendEmail: true,
      });
    } catch (err) {
      setDialogError(err.response?.data?.message || "Failed to create Teams meeting");
    } finally {
      setMeetingSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await adminApi.deleteConsultation(selectedConsultation._id);
      setSuccess("Consultation deleted successfully");
      setOpenDeleteDialog(false);
      setSelectedConsultation(null);
      setReloadToken((t) => t + 1);
    } catch (err) {
      setDialogError(err.response?.data?.message || "Failed to delete consultation");
    }
  };

  const handleStatusChange = async (consultationId, newStatus) => {
    try {
      await adminApi.updateConsultation(consultationId, { status: newStatus });
      setSuccess("Status updated successfully");
      setReloadToken((t) => t + 1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "SCHEDULED":
        return "info";
      case "COMPLETED":
        return "success";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    { Header: "name", accessor: "name", width: "15%", align: "left" },
    { Header: "email", accessor: "email", width: "20%", align: "left" },
    { Header: "phone", accessor: "phone", width: "12%", align: "left" },
    { Header: "service", accessor: "service", width: "15%", align: "left" },
    { Header: "preferred date", accessor: "preferredDate", width: "12%", align: "left" },
    { Header: "status", accessor: "status", width: "12%", align: "center" },
    { Header: "actions", accessor: "actions", width: "14%", align: "center" },
  ];

  const renderRow = (consultation) => ({
    name: consultation.name,
    email: consultation.email,
    phone: (
      <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={0.5}>
        {consultation.phone || "N/A"}
        <CallButton phoneNumber={consultation.phone} />
      </MDBox>
    ),
    service: consultation.service,
    preferredDate: new Date(consultation.preferredDate).toLocaleDateString(),
    status: (
      <FormControl size="small" fullWidth>
        <Select
          value={consultation.status}
          onChange={(e) => handleStatusChange(consultation._id, e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="PENDING">
            <Chip label="PENDING" color="warning" size="small" />
          </MenuItem>
          <MenuItem value="SCHEDULED">
            <Chip label="SCHEDULED" color="info" size="small" />
          </MenuItem>
          <MenuItem value="COMPLETED">
            <Chip label="COMPLETED" color="success" size="small" />
          </MenuItem>
          <MenuItem value="CANCELLED">
            <Chip label="CANCELLED" color="error" size="small" />
          </MenuItem>
        </Select>
      </FormControl>
    ),
    actions: (
      <MDBox display="flex" justifyContent="center" gap={0.5}>
        <Tooltip title="View details">
          <IconButton
            onClick={() => handleViewDetails(consultation)}
            size="small"
            sx={ROW_ACTION_SX}
          >
            <Icon>visibility</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Create Teams meeting">
          <IconButton
            onClick={() => openMeetingDialog(consultation)}
            size="small"
            sx={{ ...ROW_ACTION_SX, color: "#0078d4" }}
          >
            <Icon>videocam</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Create payment link">
          <IconButton
            onClick={() => setPaymentLinkTarget(consultation)}
            size="small"
            sx={{ ...ROW_ACTION_SX, color: "#00996a" }}
          >
            <Icon>link</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            onClick={() => handleDeleteClick(consultation)}
            size="small"
            color="error"
            sx={ROW_ACTION_SX}
          >
            <Icon>delete</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: "0 8px 32px rgba(102, 126, 234, 0.15)",
                border: "1px solid rgba(102, 126, 234, 0.1)",
                transition: "all 0.3s ease",
              }}
            >
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={{ xs: 2, md: 3 }}
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "lg",
                  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                    pointerEvents: "none",
                  },
                }}
              >
                <MDBox display="flex" alignItems="center" gap={2}>
                  <MDBox
                    sx={{
                      width: "50px",
                      height: "50px",
                      background: "rgba(255, 255, 255, 0.2)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <Icon sx={{ color: "white", fontSize: 28 }}>calendar_today</Icon>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="bold" color="white">
                      Consultation Requests
                    </MDTypography>
                    <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                      Manage and track all consultation bookings
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>

              {error && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="error" dismissible onClose={() => setError("")}>
                    {error}
                  </MDAlert>
                </MDBox>
              )}

              {success && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
                    {success}
                  </MDAlert>
                </MDBox>
              )}

              <RecordTable
                columns={columns}
                fetchPage={adminApi.getConsultations}
                renderRow={renderRow}
                statusOptions={STATUS_FILTER_OPTIONS}
                reloadToken={reloadToken}
                exportConfig={EXPORT_CONFIG}
                emptyMessage="No consultations yet"
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* View Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{
          sx: {
            borderRadius: isPhone ? 0 : "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} color="inherit">
            <Icon>info</Icon>
            <span>Consultation Request Details</span>
          </MDBox>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          {selectedConsultation && (
            <>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Name:
                </MDTypography>
                <MDTypography variant="body2">{selectedConsultation.name}</MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Email:
                </MDTypography>
                <MDTypography variant="body2">{selectedConsultation.email}</MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Phone:
                </MDTypography>
                <MDTypography variant="body2">
                  {selectedConsultation.phone || "Not provided"}
                </MDTypography>
              </MDBox>
              {selectedConsultation.company && (
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Company:
                  </MDTypography>
                  <MDTypography variant="body2">{selectedConsultation.company}</MDTypography>
                </MDBox>
              )}
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Service:
                </MDTypography>
                <MDTypography variant="body2">{selectedConsultation.service}</MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Preferred Date:
                </MDTypography>
                <MDTypography variant="body2">
                  {new Date(selectedConsultation.preferredDate).toLocaleDateString()}
                </MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Preferred Time:
                </MDTypography>
                <MDTypography variant="body2">{selectedConsultation.preferredTime}</MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Message:
                </MDTypography>
                <MDTypography variant="body2">
                  {selectedConsultation.message || "No message provided"}
                </MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Status:
                </MDTypography>
                <MDBox mt={1}>
                  <Chip
                    label={selectedConsultation.status}
                    color={getStatusColor(selectedConsultation.status)}
                    size="small"
                  />
                </MDBox>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Submitted On:
                </MDTypography>
                <MDTypography variant="body2">
                  {new Date(selectedConsultation.createdAt).toLocaleString()}
                </MDTypography>
              </MDBox>
              {selectedConsultation.notes && (
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Notes:
                  </MDTypography>
                  <MDTypography variant="body2">{selectedConsultation.notes}</MDTypography>
                </MDBox>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="info">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #f44336 0%, #c62828 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} color="inherit">
            <Icon>warning</Icon>
            <span>Confirm Delete</span>
          </MDBox>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          {dialogError && (
            <MDBox mb={2}>
              <MDAlert color="error" dismissible onClose={() => setDialogError("")}>
                {dialogError}
              </MDAlert>
            </MDBox>
          )}
          <MDTypography variant="body2">
            Are you sure you want to delete this consultation request from{" "}
            <strong>{selectedConsultation?.name}</strong>?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenDeleteDialog(false)} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleDeleteConfirm} color="error">
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={meetingDialogOpen}
        onClose={() => setMeetingDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{ sx: { borderRadius: isPhone ? 0 : "16px" } }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0078d4 0%, #2b6cb0 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          Create Microsoft Teams meeting
        </DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <MDAlert color="error" dismissible onClose={() => setDialogError("")}>
              {dialogError}
            </MDAlert>
          )}
          <Stack spacing={2} mt={1}>
            <TextField
              label="Meeting title"
              fullWidth
              value={meetingForm.subject}
              onChange={(e) => setMeetingForm({ ...meetingForm, subject: e.target.value })}
            />
            <TextField
              label="Description"
              multiline
              minRows={3}
              fullWidth
              value={meetingForm.description}
              onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Start date/time"
                type="datetime-local"
                fullWidth
                value={meetingForm.startDateTime}
                onChange={(e) => setMeetingForm({ ...meetingForm, startDateTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End date/time"
                type="datetime-local"
                fullWidth
                value={meetingForm.endDateTime}
                onChange={(e) => setMeetingForm({ ...meetingForm, endDateTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Time zone"
              fullWidth
              value={meetingForm.timeZone}
              onChange={(e) => setMeetingForm({ ...meetingForm, timeZone: e.target.value })}
              helperText="The start and end times above are in this zone (e.g. Africa/Lagos, Europe/London)."
            />
            <TextField
              label="Invitee emails (comma/new line separated)"
              fullWidth
              multiline
              minRows={2}
              value={meetingForm.attendees}
              onChange={(e) => setMeetingForm({ ...meetingForm, attendees: e.target.value })}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Meeting type</InputLabel>
                <Select
                  value={meetingForm.meetingType}
                  label="Meeting type"
                  onChange={(e) => setMeetingForm({ ...meetingForm, meetingType: e.target.value })}
                >
                  <MenuItem value="one-time">One-time</MenuItem>
                  <MenuItem value="recurring">Recurring</MenuItem>
                </Select>
              </FormControl>
              {meetingForm.meetingType === "recurring" && (
                <>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Recurrence pattern</InputLabel>
                    <Select
                      value={meetingForm.recurrencePattern}
                      label="Recurrence pattern"
                      onChange={(e) =>
                        setMeetingForm({ ...meetingForm, recurrencePattern: e.target.value })
                      }
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="absoluteMonthly">Monthly</MenuItem>
                      <MenuItem value="relativeMonthly">Relative monthly</MenuItem>
                      <MenuItem value="absoluteYearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Interval"
                    type="number"
                    fullWidth
                    value={meetingForm.recurrenceInterval}
                    onChange={(e) =>
                      setMeetingForm({ ...meetingForm, recurrenceInterval: e.target.value })
                    }
                  />
                </>
              )}
            </Stack>
            {meetingForm.meetingType === "recurring" && (
              <TextField
                label="Recurrence end date"
                type="date"
                fullWidth
                value={meetingForm.recurrenceEndDate}
                onChange={(e) =>
                  setMeetingForm({ ...meetingForm, recurrenceEndDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={meetingForm.sendEmail}
                  onChange={(e) => setMeetingForm({ ...meetingForm, sendEmail: e.target.checked })}
                />
              }
              label="Send invitation email with .ics attachment"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton onClick={() => setMeetingDialogOpen(false)} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleCreateMeeting} color="info" disabled={meetingSubmitting}>
            {meetingSubmitting ? "Creating..." : "Create meeting"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <PaymentLinkModal
        open={!!paymentLinkTarget}
        onClose={() => setPaymentLinkTarget(null)}
        prefill={{
          name: paymentLinkTarget?.name || "",
          email: paymentLinkTarget?.email || "",
          phone: paymentLinkTarget?.phone || "",
          description: `Consultation – ${
            paymentLinkTarget?.service || paymentLinkTarget?.name || ""
          }`,
        }}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Consultations;
